import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'components/UIElements'
import { browserHistory } from 'utils/navigation'
import {
  getScheduleExpireValue,
  getScheduleFrequencyValue,
  getScheduleSendToValue,
  getScheduleStartTypeLabel,
  getScheduleStartValue,
  getScheduleVisibilityValue,
} from '../../InstrumentPreview/utils/schedulePreviewHelpers'
import { getScheduleId } from '../../../../../utils/multiSchedule'
import '../stylesheets/AllSchedules.scss'

const DETAIL_FIELDS = [
  {
    key: 'start',
    label: 'Start',
    iconClass: 'far fa-clock',
    iconTone: 'start',
    getValue: (schedule, metadata, _cohortMap, _siteSelectionList, nameContext) =>
      getScheduleStartValue(schedule, metadata?.article_type, metadata?.deploy_on_confirm, nameContext),
  },
  {
    key: 'frequency',
    label: 'Frequency',
    iconClass: 'far fa-clock',
    iconTone: 'frequency',
    getValue: schedule => getScheduleFrequencyValue(schedule),
  },
  {
    key: 'sendTo',
    label: 'Send to',
    iconClass: 'fas fa-users',
    iconTone: 'send-to',
    getValue: (schedule, _metadata, cohortMap, siteSelectionList) =>
      getScheduleSendToValue(schedule, cohortMap, siteSelectionList),
  },
  {
    key: 'expire',
    label: 'Expire',
    iconClass: 'far fa-calendar-alt',
    iconTone: 'expire',
    getValue: schedule => getScheduleExpireValue(schedule),
  },
  {
    key: 'visibility',
    label: 'Visibility',
    iconClass: 'far fa-eye',
    iconTone: 'visibility',
    getValue: (schedule, metadata) => getScheduleVisibilityValue(schedule, metadata),
  },
]

const ScheduleItem = ({
  schedule,
  index,
  metadata,
  cohortMap,
  siteSelectionList,
  nameContext,
  defaultOpen,
  canDelete,
  canWrite,
  isReadOnlyState,
  onEditSchedule,
  onDeleteSchedule,
}) => {
  const [open, setOpen] = useState(!!defaultOpen)
  const startTypeLabel = getScheduleStartTypeLabel(schedule)
  const scheduleTitle = `Schedule ${index + 1}`
  const showActions = canWrite || isReadOnlyState

  return (
    <div className={`schedule-item${open ? ' open' : ''}`}>
      <button
        type='button'
        className='schedule-item-header'
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}>
        <span className='schedule-item-title'>
          <i
            className={`fas ${open ? 'fa-caret-down' : 'fa-caret-right'} schedule-item-chevron`}
            aria-hidden='true'
          />
          <span className='schedule-item-name'>{scheduleTitle}</span>
        </span>
        <span className='schedule-type-tag'>
          <i className='far fa-clock' aria-hidden='true' />
          <span>{startTypeLabel}</span>
        </span>
      </button>
      {open && (
        <div className='schedule-item-body'>
          <div className='schedule-details-grid'>
            {DETAIL_FIELDS.map(field => (
              <div key={field.key} className='schedule-detail'>
                <span className={`schedule-detail-icon ${field.iconTone}`}>
                  <i className={field.iconClass} aria-hidden='true' />
                </span>
                <div className='schedule-detail-text'>
                  <span className='schedule-detail-label'>{field.label}</span>
                  <span className='schedule-detail-value'>
                    {field.getValue(schedule, metadata, cohortMap, siteSelectionList, nameContext)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {showActions && (
            <div className='schedule-item-actions'>
              {canDelete && (
                <Button
                  id={`delete-schedule-${index}`}
                  className='no-margin schedule-action-btn schedule-delete-btn'
                  content='Delete Schedule'
                  onClick={e => {
                    e.stopPropagation()
                    onDeleteSchedule(schedule, index)
                  }}
                />
              )}
              <Button
                id={`${isReadOnlyState ? 'view' : 'edit'}-schedule-${index}`}
                className='no-margin schedule-action-btn schedule-edit-btn'
                content={isReadOnlyState ? 'View Schedule' : 'Edit Schedule'}
                onClick={e => {
                  e.stopPropagation()
                  onEditSchedule(schedule, index)
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

ScheduleItem.propTypes = {
  schedule: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  metadata: PropTypes.object,
  cohortMap: PropTypes.objectOf(PropTypes.string),
  siteSelectionList: PropTypes.array,
  nameContext: PropTypes.object,
  defaultOpen: PropTypes.bool,
  canDelete: PropTypes.bool,
  canWrite: PropTypes.bool,
  isReadOnlyState: PropTypes.bool,
  onEditSchedule: PropTypes.func,
  onDeleteSchedule: PropTypes.func,
}

const AllSchedulesPage = ({
  instrument,
  confirmedSchedules = [],
  cohortMap,
  siteSelectionList,
  nameContext,
  studyID,
  studyVersionID,
  isReadOnlyState,
  hasMultiSchedule = false,
  onNewSchedule,
  onEditSchedule,
  onDeleteSchedule,
  onScheduleInstrument,
  isDeployDisabled,
}) => {
  const metadata = instrument?.metadata || {}
  const schedules = confirmedSchedules
  const scheduleCount = schedules.length
  const canWrite = !isReadOnlyState
  const canAddSchedule = canWrite && hasMultiSchedule
  const canDeleteSchedule = canWrite && hasMultiSchedule && scheduleCount > 1
  const latestIndex = scheduleCount > 0 ? scheduleCount - 1 : -1

  return (
    <div className='all-schedules page'>
      <div className='all-schedules-header'>
        <h3>All Schedules</h3>
        {canAddSchedule && (
          <Button
            id='new-schedule'
            className='no-margin new-schedule-btn'
            content='+ New Schedule'
            onClick={onNewSchedule}
          />
        )}
      </div>

      {scheduleCount > 1 && (
        <div className='schedule-info-banner'>
          <p>
            This instrument has {scheduleCount} schedules. You can expand each schedule to view details
            or edit settings.
          </p>
        </div>
      )}

      {!scheduleCount ? (
        <p className='schedule-empty'>No schedule configured for this instrument.</p>
      ) : (
        <div className='schedule-list'>
          {schedules.map((schedule, index) => (
            <ScheduleItem
              key={getScheduleId(schedule) || `schedule-${index}`}
              schedule={schedule}
              index={index}
              metadata={metadata}
              cohortMap={cohortMap}
              siteSelectionList={siteSelectionList}
              nameContext={nameContext}
              defaultOpen={index === latestIndex}
              canDelete={canDeleteSchedule}
              canWrite={canWrite}
              isReadOnlyState={isReadOnlyState}
              onEditSchedule={onEditSchedule}
              onDeleteSchedule={onDeleteSchedule}
            />
          ))}
        </div>
      )}

      <div className='button-list'>
        <Button
          grey
          id='cancel'
          content='Cancel'
          onClick={() => browserHistory.push(`/studies/${studyID}/${studyVersionID}/instruments`)}
        />
        {canWrite && scheduleCount > 0 && (
          <Button
            id='schedule-instrument'
            className='deploy-button'
            content='Schedule Instrument'
            onClick={onScheduleInstrument}
            disabled={isDeployDisabled}
          />
        )}
      </div>
    </div>
  )
}

AllSchedulesPage.propTypes = {
  instrument: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    metadata: PropTypes.object,
  }),
  confirmedSchedules: PropTypes.arrayOf(PropTypes.object),
  cohortMap: PropTypes.objectOf(PropTypes.string),
  siteSelectionList: PropTypes.array,
  nameContext: PropTypes.object,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnlyState: PropTypes.bool,
  hasMultiSchedule: PropTypes.bool,
  onNewSchedule: PropTypes.func,
  onEditSchedule: PropTypes.func,
  onDeleteSchedule: PropTypes.func,
  onScheduleInstrument: PropTypes.func,
  isDeployDisabled: PropTypes.bool,
}

export default AllSchedulesPage
